import { ref, watch, type Ref } from "vue";
import { ElMessage } from "element-plus";
import {
  getProjects as getProjectsApi,
  deployEnv as deployEnvApi,
  deployProject as deployProjectApi,
  destroyProject as destroyProjectApi,
  addProject as addProjectApi,
  updateProject as updateProjectApi,
  detailProjectApi,
} from "../api/envApi";
import type { ProjectForm, Project } from "../types";

export function useProjects(activeIndex: Ref<number>) {
  const projects = ref<Project[]>([]);
  const loading = ref(false);
  const submitting = ref(false);

  // 加载项目列表
  const loadProjects = async (envId: number, notLoading: boolean = false) => {
    if (!notLoading) {
      loading.value = true;
      projects.value = [];
    }
    try {
      const result = await getProjectsApi(envId);
      projects.value = result;
    } catch (error) {
      ElMessage.error(error || "加载项目列表失败");
    } finally {
      loading.value = false;
    }
  };

  // 一键部署所有项目
  const deployEnv = async (envId: number) => {
    try {
      await deployEnvApi(envId);
      ElMessage.success("已触发环境部署");
      setTimeout(() => {
        loadProjects(envId, true);
      }, 5000);
    } catch (error) {
      ElMessage.error(error || "部署环境失败");
    }
  };

  let setTimeoutId: number | null = null;
  const updateProjectSucess = async (id: number) => {
    try {
      const res = await detailProjectApi({ id });
      if (res) {
        let index = projects.value.findIndex((item) => item.id === id);
        if (index !== -1) {
          projects.value[index] = res;
        }
        if (res.status === "deploySuccess" || res.status === "destroyFailure") {
          setTimeoutId && clearInterval(setTimeoutId);
        }
      }
    } catch (error) {}
  };

  // 部署单个项目
  const deployProject = async (projectId: number) => {
    try {
      await deployProjectApi(projectId);
      ElMessage.success("已触发项目部署");
      if (setTimeoutId) {
        clearInterval(setTimeoutId);
      }
      setTimeoutId = setInterval(() => {
        updateProjectSucess(projectId);
      }, 1000);
    } catch (error) {
      ElMessage.error(error || "部署项目失败");
    }
  };

  // 删除项目
  const destroyProject = async (projectId: number, envId: number) => {
    try {
      await destroyProjectApi(projectId);
      ElMessage.success("项目已销毁");
      setTimeout(() => {
        loadProjects(envId, true);
      }, 2000);
    } catch (error) {
      ElMessage.error(error || "销毁项目失败");
    }
  };

  // 创建项目
  const createProject = async (form: ProjectForm) => {
    submitting.value = true;
    try {
      await addProjectApi(form);
      ElMessage.success("项目添加成功");
      return true;
    } catch (error) {
      ElMessage.error(error || "添加项目失败");
      return false;
    } finally {
      submitting.value = false;
    }
  };

  // 编辑项目
  const updateProject = async (
    data: Partial<Project> & { id: number; envID: number },
  ) => {
    submitting.value = true;
    try {
      await updateProjectApi(data);
      ElMessage.success("项目更新成功");
      return true;
    } catch (error) {
      ElMessage.error(error || "更新项目失败");
      return false;
    } finally {
      submitting.value = false;
    }
  };

  // 增加定时器，定时更新项目状态
  let setTimeoutId2: number | null = null;
  // 定时器函数
  function updateProjectStatus() {
    if (!activeIndex.value) return;
    setTimeoutId2 && clearInterval(setTimeoutId2);
    setTimeoutId2 = setInterval(() => {
      loadProjects(activeIndex.value, true);
    }, 5000);
  }

  watch(activeIndex, updateProjectStatus);

  return {
    projects,
    loading,
    submitting,
    loadProjects,
    createProject,
    updateProject,
    deployEnv,
    deployProject,
    destroyProject,
  };
}
